import { useEffect, useRef, useState } from "react";
import * as THREE from "three";

/**
 * AtomARScene — Interactive 3D atom/solar-system scene.
 *
 * Renders each `item` as an orbiting planet around a central sun/nucleus.
 * Users can drag to rotate, pinch/scroll to zoom, and click any planet to
 * select it (opens the parent's editor/viewer). Communication links between
 * consecutive planets are drawn as glowing lines.
 *
 * Props:
 *   - items:       array of { id, title, image_url }
 *   - onSelect(it) callback fired when a planet is clicked
 *   - arMode:      bool — when true uses transparent canvas with device camera
 *                        as background (pseudo-AR)
 *   - height:      canvas height in px (default 480)
 */
export default function AtomARScene({ items = [], onSelect, arMode = false, height = 480 }) {
  const mountRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const planetsRef = useRef([]);
  const rafRef = useRef(null);
  const videoRef = useRef(null);
  const [cameraErr, setCameraErr] = useState(null);

  // Attach camera stream for AR background
  useEffect(() => {
    let stream = null;
    if (arMode) {
      (async () => {
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { ideal: "environment" } },
            audio: false,
          });
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            await videoRef.current.play().catch(() => {});
          }
        } catch (e) {
          setCameraErr("No se pudo abrir la cámara — permite el permiso e intenta nuevamente");
        }
      })();
    }
    return () => {
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
  }, [arMode]);

  useEffect(() => {
    if (!mountRef.current) return;
    const mount = mountRef.current;
    const width = mount.clientWidth || 400;
    const h = height;

    const scene = new THREE.Scene();
    sceneRef.current = scene;
    if (!arMode) scene.background = new THREE.Color(0x050510);

    const camera = new THREE.PerspectiveCamera(55, width / h, 0.1, 1000);
    camera.position.set(0, 4, 12);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: arMode });
    rendererRef.current = renderer;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, h);
    renderer.setClearColor(0x050510, arMode ? 0 : 1);
    mount.appendChild(renderer.domElement);

    // Starfield
    if (!arMode) {
      const starGeo = new THREE.BufferGeometry();
      const starCount = 400;
      const positions = new Float32Array(starCount * 3);
      for (let i = 0; i < starCount; i++) {
        positions[i * 3] = (Math.random() - 0.5) * 200;
        positions[i * 3 + 1] = (Math.random() - 0.5) * 100;
        positions[i * 3 + 2] = (Math.random() - 0.5) * 200;
      }
      starGeo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      const stars = new THREE.Points(
        starGeo,
        new THREE.PointsMaterial({ color: 0xffffff, size: 0.15, transparent: true, opacity: 0.8 })
      );
      scene.add(stars);
    }

    // Ambient & sun light
    scene.add(new THREE.AmbientLight(0xffffff, 0.35));
    const sunLight = new THREE.PointLight(0xffcc66, 3.2, 60);
    sunLight.position.set(0, 0, 0);
    scene.add(sunLight);

    // Sun (nucleus) — emissive glowing sphere
    const sun = new THREE.Mesh(
      new THREE.SphereGeometry(1.1, 48, 48),
      new THREE.MeshStandardMaterial({
        color: 0xffcc66,
        emissive: 0xff8833,
        emissiveIntensity: 1.4,
        roughness: 0.6,
      })
    );
    sun.userData.type = "sun";
    scene.add(sun);
    // Glow halo
    const halo = new THREE.Mesh(
      new THREE.SphereGeometry(1.4, 32, 32),
      new THREE.MeshBasicMaterial({ color: 0xffaa33, transparent: true, opacity: 0.18 })
    );
    scene.add(halo);

    // Create planets in orbit — one per item
    planetsRef.current = [];
    const loader = new THREE.TextureLoader();
    loader.setCrossOrigin("anonymous");
    const palette = [0xccff00, 0x00a0ff, 0xff6b00, 0xe63946, 0x7c4dff, 0x00c853, 0xffea00, 0x64c8ff];
    const orbitLineGroup = new THREE.Group();
    scene.add(orbitLineGroup);

    (items || []).forEach((it, idx) => {
      const radius = 3 + idx * 1.5;
      const speed = 0.15 - idx * 0.012;
      const size = 0.32 + Math.random() * 0.15;
      // Orbit ring
      const ringGeo = new THREE.RingGeometry(radius - 0.02, radius + 0.02, 96);
      const ring = new THREE.Mesh(
        ringGeo,
        new THREE.MeshBasicMaterial({ color: 0xccff00, transparent: true, opacity: 0.22, side: THREE.DoubleSide })
      );
      ring.rotation.x = Math.PI / 2;
      orbitLineGroup.add(ring);

      const mat = new THREE.MeshStandardMaterial({
        color: palette[idx % palette.length],
        emissive: palette[idx % palette.length],
        emissiveIntensity: 0.35,
        roughness: 0.55,
        metalness: 0.15,
      });
      const planet = new THREE.Mesh(new THREE.SphereGeometry(size, 40, 40), mat);
      planet.userData = { type: "planet", id: it.id, idx, radius, speed, phase: Math.random() * Math.PI * 2, item: it };
      scene.add(planet);
      planetsRef.current.push(planet);

      // Load image as texture (if URL is CORS-friendly)
      if (it.image_url) {
        loader.load(
          it.image_url,
          (tex) => { tex.colorSpace = THREE.SRGBColorSpace; mat.map = tex; mat.emissiveIntensity = 0.15; mat.needsUpdate = true; },
          undefined,
          () => { /* silent */ }
        );
      }
    });

    // Communication links between consecutive planets (glowing lines)
    const linkGroup = new THREE.Group();
    scene.add(linkGroup);

    // Raycaster for click selection
    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();
    let hovered = null;

    function updatePointer(ev) {
      const rect = renderer.domElement.getBoundingClientRect();
      const cx = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const cy = ev.touches ? ev.touches[0].clientY : ev.clientY;
      pointer.x = ((cx - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((cy - rect.top) / rect.height) * 2 + 1;
    }
    function onClick(ev) {
      updatePointer(ev);
      raycaster.setFromCamera(pointer, camera);
      const hit = raycaster.intersectObjects(planetsRef.current, false)[0];
      if (hit && hit.object.userData.item) {
        onSelect?.(hit.object.userData.item);
      }
    }
    function onMove(ev) {
      updatePointer(ev);
      raycaster.setFromCamera(pointer, camera);
      const hit = raycaster.intersectObjects(planetsRef.current, false)[0];
      const next = hit?.object || null;
      if (hovered !== next) {
        if (hovered) hovered.scale.set(1, 1, 1);
        if (next) next.scale.set(1.35, 1.35, 1.35);
        hovered = next;
        renderer.domElement.style.cursor = next ? "pointer" : "grab";
      }
    }
    renderer.domElement.addEventListener("click", onClick);
    renderer.domElement.addEventListener("mousemove", onMove);
    renderer.domElement.addEventListener("touchend", onClick);
    renderer.domElement.style.cursor = "grab";

    // Simple drag-to-rotate + wheel zoom (orbit controls without a dep)
    let drag = null;
    let camAngleY = 0, camAngleX = 0.4, camDist = 13;
    function applyCam() {
      const y = Math.max(-1.2, Math.min(1.2, camAngleX));
      camera.position.x = camDist * Math.sin(camAngleY) * Math.cos(y);
      camera.position.z = camDist * Math.cos(camAngleY) * Math.cos(y);
      camera.position.y = camDist * Math.sin(y);
      camera.lookAt(0, 0, 0);
    }
    applyCam();
    function onDown(ev) {
      const cx = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const cy = ev.touches ? ev.touches[0].clientY : ev.clientY;
      drag = { x: cx, y: cy };
      renderer.domElement.style.cursor = "grabbing";
    }
    function onDrag(ev) {
      if (!drag) return;
      const cx = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const cy = ev.touches ? ev.touches[0].clientY : ev.clientY;
      camAngleY += (cx - drag.x) * 0.008;
      camAngleX += (cy - drag.y) * 0.006;
      drag = { x: cx, y: cy };
      applyCam();
    }
    function onUp() { drag = null; renderer.domElement.style.cursor = "grab"; }
    function onWheel(ev) {
      ev.preventDefault();
      camDist = Math.max(6, Math.min(30, camDist + ev.deltaY * 0.01));
      applyCam();
    }
    renderer.domElement.addEventListener("mousedown", onDown);
    renderer.domElement.addEventListener("mousemove", onDrag);
    window.addEventListener("mouseup", onUp);
    renderer.domElement.addEventListener("touchstart", onDown);
    renderer.domElement.addEventListener("touchmove", onDrag);
    window.addEventListener("touchend", onUp);
    renderer.domElement.addEventListener("wheel", onWheel, { passive: false });

    // Handle resize
    function onResize() {
      const w = mount.clientWidth || 400;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    }
    window.addEventListener("resize", onResize);

    // Animate
    const clock = new THREE.Clock();
    function animate() {
      const dt = clock.getDelta();
      const t = clock.getElapsedTime();
      sun.rotation.y += dt * 0.15;
      halo.rotation.y -= dt * 0.05;
      halo.scale.setScalar(1 + Math.sin(t * 2) * 0.03);

      // Update planet positions
      planetsRef.current.forEach((p) => {
        const u = p.userData;
        u.phase += dt * u.speed;
        p.position.set(Math.cos(u.phase) * u.radius, Math.sin(t + u.idx) * 0.25, Math.sin(u.phase) * u.radius);
        p.rotation.y += dt * 0.3;
      });

      // Rebuild communication links every frame
      while (linkGroup.children.length) linkGroup.remove(linkGroup.children[0]);
      for (let i = 0; i < planetsRef.current.length - 1; i++) {
        const a = planetsRef.current[i].position;
        const b = planetsRef.current[i + 1].position;
        const geo = new THREE.BufferGeometry().setFromPoints([a, b]);
        const line = new THREE.Line(
          geo,
          new THREE.LineBasicMaterial({ color: 0xccff00, transparent: true, opacity: 0.35 })
        );
        linkGroup.add(line);
      }

      renderer.render(scene, camera);
      rafRef.current = requestAnimationFrame(animate);
    }
    animate();

    // Cleanup
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
      try { renderer.domElement.removeEventListener("click", onClick); } catch {}
      try { mount.removeChild(renderer.domElement); } catch {}
      renderer.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, arMode, height]);

  return (
    <div className="relative rounded-lg overflow-hidden border border-[#CCFF00]/30 bg-black" style={{ height }} data-testid="atom-ar-scene">
      {arMode && (
        <video
          ref={videoRef}
          playsInline
          muted
          className="absolute inset-0 w-full h-full object-cover"
          style={{ pointerEvents: "none" }}
        />
      )}
      <div ref={mountRef} className="absolute inset-0" />
      <div className="absolute top-2 left-2 px-2 py-1 rounded bg-black/60 border border-white/10 text-[9px] uppercase tracking-widest text-[#CCFF00] font-bold pointer-events-none">
        {arMode ? "🎥 AR + 3D" : "🌌 3D · Arrastra para rotar · Rueda para zoom"}
      </div>
      {arMode && cameraErr && (
        <div className="absolute bottom-2 left-2 right-2 px-3 py-2 rounded bg-red-500/20 border border-red-500/40 text-[10px] text-red-300 font-bold">
          {cameraErr}
        </div>
      )}
      {(items || []).length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center text-gray-500 text-sm pointer-events-none">
          Agrega planetas para verlos en órbita
        </div>
      )}
    </div>
  );
}
